import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Table from "../../components/admin/Table";
import { ReservationManageService } from "../../services/ReservationManageService";
import { SchoolService } from "../../services/SchoolService";
import { DateTime } from "luxon";

const reservationService = new ReservationManageService();
const schoolService = new SchoolService();

export default function Reservations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    username: "",
    name: "",
    schoolId: "",
    status: "",
  });
  const [orderBy, setOrderBy] = useState("date");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [schools, setSchools] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [duplicateReservations, setDuplicateReservations] = useState([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const itemsPerPage = 100;

  // Check if user is from "Comando" role
  const isComandoRole = localStorage.getItem("roleName") === "Comando";

  const fetchReservations = useCallback(
    (page = 1, appliedFilters = {}, sortBy = orderBy, sortDirection = orderDirection) => {
      setLoading(true);
      const params = {
        itemsPerPage: itemsPerPage,
        page: page,
        orderBy: sortBy,
        orderDirection: sortDirection,
      };

      if (appliedFilters.dateFrom) params.dateFrom = appliedFilters.dateFrom;
      if (appliedFilters.dateTo) params.dateTo = appliedFilters.dateTo;
      if (appliedFilters.username) params.username = appliedFilters.username;
      if (appliedFilters.name) params.name = appliedFilters.name;
      if (appliedFilters.schoolId) params.schoolId = appliedFilters.schoolId;
      if (appliedFilters.status) params.status = appliedFilters.status;

      reservationService
        .find(params)
        .then((response) => {
          console.log("response", response);
          console.log(
            "Total items:",
            response.totalItems,
            "Items in page:",
            response.items.length
          );
          const reservationList = response.items.map((item) => ({
            ...item,
            formattedDate: formatDateTime(item.startDate, item.endDate),
            fullName: `${item.user?.name || ""} ${item.user?.surname || ""} ${
              item.user?.lastName || ""
            }`
              .trim()
              .replace(/\s{2,}/g, " "),
          }));

          setReservations(reservationList);

          const actualTotal = response.totalItems || 0;
          setTotalItems(actualTotal);

          const calculatedTotalPages = Math.ceil(actualTotal / itemsPerPage);
          console.log(
            "Total pages calculated:",
            calculatedTotalPages,
            "from total:",
            actualTotal
          );
          setTotalPages(calculatedTotalPages);
          setCurrentPage(page);

          const urlParams = {
            page: page.toString(),
            orderBy: sortBy,
            orderDirection: sortDirection
          };
          Object.keys(appliedFilters).forEach((key) => {
            if (appliedFilters[key]) {
              urlParams[key] = appliedFilters[key];
            }
          });
          setSearchParams(urlParams);
        })
        .catch((error) => {
          console.error("Erro ao buscar reservas:", error);
          alert("Não foi possível carregar as reservas.");
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [itemsPerPage, setSearchParams, orderBy, orderDirection]
  );

  const formatDateTime = (startDate, endDate) => {
    try {
      const start = DateTime.fromISO(startDate, { zone: "UTC" })
        .setZone("America/Sao_Paulo")
        .setLocale("pt-BR")
        .toFormat("ccc dd/MM/yyyy HH:mm")
        .toUpperCase()
        .replace(/\./, "");

      const end = DateTime.fromISO(endDate, { zone: "UTC" })
        .setZone("America/Sao_Paulo")
        .toFormat("HH:mm");

      return `${start} ÀS ${end}`;
    } catch (error) {
      console.error("Error formatting datetime:", error);
      return "Data inválida";
    }
  };

  useEffect(() => {
    schoolService
      .find({ pagination: false })
      .then((response) => {
        setSchools(response.items || []);
      })
      .catch((error) => {
        console.error("Error fetching schools:", error);
      });

    // NOTE: the per-render `getDuplicateReservations()` call has been
    // removed. The DB-level unique partial index in the new model
    // (modules/reservation-v2) makes new duplicates impossible, and
    // the legacy collection has been backfilled clean. A nightly job
    // (TODO: api/src/jobs/duplicate-check.job.ts) will canary for any
    // future drift and alert via the activity log. Re-add an
    // admin-triggered "Check now" button if a manual re-scan is ever
    // needed (the endpoint at GET /reservations/duplicates still
    // exists for that case).

    const pageParam = searchParams.get("page");
    const initialPage = pageParam ? parseInt(pageParam, 10) : 1;

    const urlFilters = {
      dateFrom: searchParams.get("dateFrom") || "",
      dateTo: searchParams.get("dateTo") || "",
      username: searchParams.get("username") || "",
      name: searchParams.get("name") || "",
      schoolId: searchParams.get("schoolId") || "",
      status: searchParams.get("status") || "",
    };

    const urlOrderBy = searchParams.get("orderBy") || "date";
    const urlOrderDirection = searchParams.get("orderDirection") || "desc";

    setFilters(urlFilters);
    setOrderBy(urlOrderBy);
    setOrderDirection(urlOrderDirection);
    fetchReservations(initialPage, urlFilters, urlOrderBy, urlOrderDirection);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchReservations(newPage, filters, orderBy, orderDirection);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuickDateRange = (range) => {
    if (!range) return;

    const now = DateTime.now().setZone("America/Sao_Paulo");
    let dateFrom, dateTo;

    switch (range) {
      case "today":
        dateFrom = now.toISODate();
        dateTo = now.toISODate();
        break;
      case "yesterday":
        dateFrom = now.minus({ days: 1 }).toISODate();
        dateTo = now.minus({ days: 1 }).toISODate();
        break;
      case "tomorrow":
        dateFrom = now.plus({ days: 1 }).toISODate();
        dateTo = now.plus({ days: 1 }).toISODate();
        break;
      case "thisWeek":
        dateFrom = now.startOf("week").toISODate();
        dateTo = now.endOf("week").toISODate();
        break;
      case "lastWeek":
        dateFrom = now.minus({ weeks: 1 }).startOf("week").toISODate();
        dateTo = now.minus({ weeks: 1 }).endOf("week").toISODate();
        break;
      case "nextWeek":
        dateFrom = now.plus({ weeks: 1 }).startOf("week").toISODate();
        dateTo = now.plus({ weeks: 1 }).endOf("week").toISODate();
        break;
      case "thisMonth":
        dateFrom = now.startOf("month").toISODate();
        dateTo = now.endOf("month").toISODate();
        break;
      case "lastMonth":
        dateFrom = now.minus({ months: 1 }).startOf("month").toISODate();
        dateTo = now.minus({ months: 1 }).endOf("month").toISODate();
        break;
      default:
        return;
    }

    setFilters((prev) => ({
      ...prev,
      dateFrom,
      dateTo,
    }));
  };

  const handleApplyFilters = () => {
    setIsFiltering(true);
    fetchReservations(1, filters, orderBy, orderDirection);
    setTimeout(() => setIsFiltering(false), 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      dateFrom: "",
      dateTo: "",
      username: "",
      name: "",
      schoolId: "",
      status: "",
    };
    setFilters(clearedFilters);
    fetchReservations(1, clearedFilters, orderBy, orderDirection);
  };

  const handleFixDuplicates = async (reservationIds) => {
    if (!window.confirm("Tem certeza que deseja corrigir estas reservas duplicadas? Apenas uma reserva será mantida.")) {
      return;
    }

    try {
      const result = await reservationService.fixDuplicateReservations(reservationIds);
      alert(`Duplicatas corrigidas com sucesso! ${result.deletedCount} reserva(s) removida(s).`);

      const duplicates = await reservationService.getDuplicateReservations();
      if (duplicates && duplicates.length > 0) {
        setDuplicateReservations(duplicates);
      } else {
        setDuplicateReservations([]);
        setShowDuplicateAlert(false);
      }

      fetchReservations(currentPage, filters);
    } catch (error) {
      console.error("Error fixing duplicates:", error);
      alert("Erro ao corrigir duplicatas. Verifique se as reservas são do mesmo usuário.");
    }
  };

  // ---- Multi-select logic ----

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === reservations.length && reservations.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(reservations.map((r) => r._id)));
    }
  };

  const handleSelectCurrentMonth = async () => {
    const now = DateTime.now().setZone("America/Sao_Paulo");
    const dateFrom = now.startOf("month").toISODate();
    const dateTo = now.endOf("month").toISODate();

    const updatedFilters = { ...filters, dateFrom, dateTo };
    setFilters(updatedFilters);

    try {
      // Fetch all IDs for current month (no pagination limit)
      const response = await reservationService.find({
        ...updatedFilters,
        itemsPerPage: 9999,
        page: 1,
        orderBy,
        orderDirection,
      });

      const ids = new Set(response.items.map((item) => item._id));
      setSelectedIds(ids);

      // Also refresh the visible table with the current month filter
      fetchReservations(1, updatedFilters, orderBy, orderDirection);
    } catch (error) {
      console.error("Error selecting current month reservations:", error);
      alert("Erro ao selecionar reservas do mês atual.");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setShowDeleteModal(false);
    setIsDeleting(true);

    try {
      const result = await reservationService.deleteMany(Array.from(selectedIds));
      alert(`${result.deletedCount} reserva(s) excluída(s) com sucesso!`);
      setSelectedIds(new Set());
      fetchReservations(currentPage, filters, orderBy, orderDirection);
    } catch (error) {
      console.error("Erro ao excluir reservas:", error);
      alert("Não foi possível excluir as reservas selecionadas.");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---- Table columns ----

  const allVisibleSelected =
    reservations.length > 0 && reservations.every((r) => selectedIds.has(r._id));

  const columns = [
    {
      label: (
        <input
          type="checkbox"
          checked={allVisibleSelected}
          onChange={toggleSelectAll}
          title="Selecionar todos visíveis"
        />
      ),
      field: "_id",
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row._id)}
          onChange={() => toggleSelectRow(row._id)}
        />
      ),
    },
    { label: "Data/Hora", field: "formattedDate" },
    { label: "Matrícula", field: "user.username" },
    { label: "Nome", field: "fullName" },
    {
      label: "Unidade Escolar",
      field: "school.name",
      render: (row) => row.school?.name || "Escola não encontrada",
    },
    {
      label: "Status",
      field: "status",
      render: (row) => {
        const statusText = row.status || "PENDENTE";
        return <span className={getStatusStyle(statusText)}>{statusText}</span>;
      },
    },
  ];

  const getStatusStyle = (status) => {
    switch (status) {
      case "CANCELADO":
        return "text-danger fw-bold";
      case "CONCLUÍDO":
        return "text-success fw-bold";
      case "EM ANDAMENTO":
        return "text-primary fw-bold";
      case "PENDENTE":
      default:
        return "text-warning fw-bold";
    }
  };

  const onAdd = () => {
    if (isComandoRole) {
      alert("Usuários do grupo Comando não podem criar reservas");
      return;
    }
    navigate("/admin/reservations/new");
  };

  const onEdit = (reservation) => {
    if (isComandoRole) {
      alert("Usuários do grupo Comando não podem editar reservas");
      return;
    }
    navigate(`/admin/reservations/${reservation._id}`);
  };

  const onDelete = (reservation) => {
    if (isComandoRole) {
      alert("Usuários do grupo Comando não podem deletar reservas");
      return;
    }

    if (
      window.confirm(
        `Tem certeza de que deseja excluir a reserva de "${reservation.fullName}"?`
      )
    ) {
      reservationService
        .delete(reservation._id)
        .then(() => {
          alert("Reserva excluída com sucesso!");
          fetchReservations(currentPage, filters);
        })
        .catch((error) => {
          console.error("Erro ao excluir reserva:", error);
          alert("Não foi possível excluir a reserva.");
        });
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Carregando reservas...</div>;
  }

  return (
    <div>
      <div className="container pt-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Reservas</h2>
          {/* Bulk delete controls */}
          {!isComandoRole && (
            <div className="d-flex gap-2 align-items-center">
              <button
                className="btn btn-outline-secondary"
                onClick={handleSelectCurrentMonth}
                title="Filtra pelo mês atual e seleciona todas as reservas"
              >
                Selecionar Mês Atual
              </button>
              {selectedIds.size > 0 && (
                <button
                  className="btn btn-danger"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? "Excluindo..."
                    : `Excluir Selecionadas (${selectedIds.size})`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Duplicate Reservations Alert */}
        {showDuplicateAlert && duplicateReservations.length > 0 && (
          <div className="alert alert-warning alert-dismissible fade show" role="alert">
            <h5 className="alert-heading">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Atenção: Reservas Duplicadas Detectadas!
            </h5>
            <p className="mb-2">
              Foram encontradas {duplicateReservations.length} situação(ões) com múltiplas reservas para a mesma escola, data e horário:
            </p>
            <div className="mt-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {duplicateReservations.map((dup, index) => (
                <div
                  key={index}
                  className={`mb-3 p-3 border-start border-4 ${
                    dup.isTomorrow
                      ? 'bg-danger bg-opacity-10 border-danger'
                      : 'bg-light border-warning'
                  }`}
                >
                  {dup.isTomorrow && (
                    <div className="badge bg-danger mb-2">
                      <i className="bi bi-exclamation-circle-fill me-1"></i>
                      AMANHÃ - AÇÃO URGENTE NECESSÁRIA
                    </div>
                  )}
                  <div className="mb-2">
                    <strong>Escola:</strong> {dup.schoolName}
                  </div>
                  <div className="mb-2">
                    <strong>Data:</strong> {dup.formattedDate}
                    {dup.isTomorrow && <span className="text-danger fw-bold ms-2">(Amanhã)</span>}
                  </div>
                  <div className="mb-2">
                    <strong>Horário:</strong> {dup.formattedStartTime} às {dup.formattedEndTime}
                  </div>
                  <div>
                    <strong>Reservas duplicadas ({dup.count}):</strong>
                    <ul className="mb-0 mt-1">
                      {dup.users.map((user, userIndex) => (
                        <li key={userIndex}>
                          {user.fullName || 'Nome não disponível'} ({user.username})
                        </li>
                      ))}
                    </ul>
                    {dup.isSameUser && dup.count > 1 && (
                      <small className="text-info">
                        <i className="bi bi-info-circle me-1"></i>
                        Mesmo usuário com {dup.count} reservas idênticas
                      </small>
                    )}
                  </div>
                  <div className="mt-3">
                    <button
                      className={`btn btn-sm ${dup.isSameUser ? 'btn-success' : 'btn-secondary'}`}
                      onClick={() => dup.isSameUser && handleFixDuplicates(dup.reservationIds)}
                      disabled={!dup.isSameUser}
                      title={dup.isSameUser
                        ? "Clique para remover duplicatas e manter apenas uma reserva"
                        : "Não é possível corrigir automaticamente - reservas de usuários diferentes"}
                    >
                      <i className={`bi ${dup.isSameUser ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>
                      {dup.isSameUser ? 'Corrigir Duplicatas' : 'Usuários Diferentes'}
                    </button>
                    {dup.isSameUser && (
                      <small className="text-muted ms-2">
                        (manterá apenas 1 reserva)
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setShowDuplicateAlert(false)}
            ></button>
          </div>
        )}

        {/* Filter Section */}
        <div className="card mb-4" style={{ border: "none", width: "100%" }}>
          <div className="card-body">
            {/* Row 1: Dates and Server */}
            <div className="row mb-3">
              <div className="col-md-7">
                <label className="form-label fw-bold">Datas</label>
                <div className="row">
                  <div className="col-3">
                    <select
                      className="form-select"
                      onChange={(e) => handleQuickDateRange(e.target.value)}
                      defaultValue=""
                    >
                      <option value="">Período Rápido...</option>
                      <option value="today">Hoje</option>
                      <option value="yesterday">Ontem</option>
                      <option value="tomorrow">Amanhã</option>
                      <option value="thisWeek">Esta Semana</option>
                      <option value="lastWeek">Semana Passada</option>
                      <option value="nextWeek">Próxima Semana</option>
                      <option value="thisMonth">Este Mês</option>
                      <option value="lastMonth">Mês Passado</option>
                    </select>
                  </div>
                  <div className="col-4">
                    <div className="input-group">
                      <span className="input-group-text">De</span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.dateFrom}
                        onChange={(e) =>
                          handleFilterChange("dateFrom", e.target.value)
                        }
                        onKeyPress={handleKeyPress}
                      />
                      <span className="input-group-text">
                        <i className="bi bi-calendar"></i>
                      </span>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="input-group">
                      <span className="input-group-text">Até</span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.dateTo}
                        onChange={(e) =>
                          handleFilterChange("dateTo", e.target.value)
                        }
                        onKeyPress={handleKeyPress}
                      />
                      <span className="input-group-text">
                        <i className="bi bi-calendar"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-5">
                <label className="form-label fw-bold">Servidor</label>
                <div className="row">
                  <div className="col-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Matrícula"
                      value={filters.username}
                      onChange={(e) =>
                        handleFilterChange("username", e.target.value)
                      }
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                  <div className="col-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nome"
                      value={filters.name}
                      onChange={(e) =>
                        handleFilterChange("name", e.target.value)
                      }
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: School, Status and Buttons */}
            <div className="row align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-bold">Unidade Escolar</label>
                <select
                  className="form-select"
                  value={filters.schoolId}
                  onChange={(e) =>
                    handleFilterChange("schoolId", e.target.value)
                  }
                >
                  <option value="">Todas as escolas</option>
                  {schools.map((school) => (
                    <option key={school._id} value={school._id}>
                      {school.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold">Status</label>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) =>
                    handleFilterChange("status", e.target.value)
                  }
                >
                  <option value="">Todos os status</option>
                  <option value="CANCELADO">CANCELADO</option>
                  <option value="EM ANDAMENTO">EM ANDAMENTO</option>
                  <option value="CONCLUÍDO">CONCLUÍDO</option>
                  <option value="PENDENTE">PENDENTE</option>
                </select>
              </div>

              <div className="col-md-5">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-primary"
                    onClick={handleApplyFilters}
                    disabled={isFiltering}
                  >
                    <i className="bi bi-funnel-fill me-2"></i>
                    Filtrar
                  </button>

                  <button
                    className="btn btn-secondary"
                    onClick={handleClearFilters}
                  >
                    <i className="bi bi-eraser-fill me-2"></i>
                    Limpar Filtros
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order By Section */}
        <div className="card mt-3" style={{ border: "none", width: "100%" }}>
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-2">
                <label className="form-label fw-bold">Ordenar por:</label>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={orderBy}
                  onChange={(e) => {
                    setOrderBy(e.target.value);
                    fetchReservations(1, filters, e.target.value, orderDirection);
                  }}
                >
                  <option value="date">Data</option>
                  <option value="userName">Nome do Usuário</option>
                  <option value="schoolName">Unidade Escolar</option>
                </select>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={orderDirection}
                  onChange={(e) => {
                    setOrderDirection(e.target.value);
                    fetchReservations(1, filters, orderBy, e.target.value);
                  }}
                >
                  <option value="asc">Crescente</option>
                  <option value="desc">Decrescente</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={reservations}
        onAdd={isComandoRole ? undefined : onAdd}
        onEdit={isComandoRole ? undefined : onEdit}
        onDelete={isComandoRole ? undefined : onDelete}
        className="mt-5"
      />

      {/* Pagination Controls */}
      <div className="container">
        <div className="d-flex flex-column justify-content-center align-items-center mt-4 mb-4">
          <div>
            <button
              className="btn btn-outline-primary me-2"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Anterior
            </button>

            <span className="mx-3 fw-bold">
              Página {currentPage} de {totalPages}
            </span>

            <button
              className="btn btn-outline-primary ms-2"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Próxima
            </button>
          </div>

          <div>
            <div className="text-muted mt-3">
              Mostrando {reservations.length} de {totalItems} reservas totais
              {selectedIds.size > 0 && (
                <span className="ms-3 text-danger fw-bold">
                  {selectedIds.size} selecionada(s)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Selected Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar Exclusão</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                />
              </div>
              <div className="modal-body">
                <p>
                  Tem certeza que deseja excluir{" "}
                  <strong>{selectedIds.size} reserva(s)</strong> selecionada(s)?
                </p>
                <p className="text-danger mb-0">
                  Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteSelected}
                >
                  Excluir {selectedIds.size} reserva(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
